import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Select } from 'antd';
import { useAxiosPrivate } from "./../../hooks/useAxiosPrivate";
import useAuth from "./../../hooks/useAuth.js";
import OkModal from "./../../components/OkModal.js";
import "./../../App.css";

const LotsForm = ({ lotsFormData, setLotsFormData, disabled }) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    const fileInputRefs = useRef([]);

    const [showOkModal, setShowOkModal] = useState(false);
    const [okMessage, setOkMessage] = useState("");
    const [typeOkModal, setTypeOkModal] = useState("success");

    const handleShowOkModal = (message, type) => {
        setOkMessage(message);
        setTypeOkModal(type);
        setShowOkModal(true);
    };

    const handleHideOkModal = () => {
        setShowOkModal(false);
    };


    console.log("lots_form.js > LotsForm > lotsFormData: ", JSON.stringify(lotsFormData, null, 2));

    useEffect(() => {
        const fetchData = async () => {
            await fetchDataForSelectMaterial();
            await fetchDataForSelectCountry();

        };
        fetchData();
    }, []);

    // Handle lot-level fields (e.g., lot_code, notes)
    const handleLotChange = (e) => {
        const { name, value } = e.target;

        if (name === 'quantity') {
            // Allow only numbers and a single decimal point
            if (/^\d*\.?\d*$/.test(value)) {
                setLotsFormData({
                    ...lotsFormData,
                    [name]: value
                });
            }
        }
        else {
            setLotsFormData({
                ...lotsFormData,
                [e.target.name]: e.target.value
            })
        };

    };


    // Handle composition field changes
    // Updated handleCompositionChange to handle both Select and input changes
    const handleCompositionChange = (index, field, value) => {

        if (field === "percentage" && !/^\d*\.?\d*$/.test(value)) {
            return; // Exit if input is not a valid number with one optional decimal point
        }

        // Create a new array to avoid mutating the state directly
        const newCompositions = [...lotsFormData.components_composition];

        // Update the field value dynamically
        newCompositions[index] = {
            ...newCompositions[index],
            [field]: value // Set the field name dynamically
        };

        // Update the state with the new compositions
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };



    // Handle supplier field changes within each composition
    const handleMaterialSupplierChange = (compositionIndex, supplierIndex, field, value) => {
        const newCompositions = [...lotsFormData.components_composition];
        const newSuppliers = [...newCompositions[compositionIndex].components_composition_supplier];
        newSuppliers[supplierIndex] = {
            ...newSuppliers[supplierIndex],
            [field]: value // Set the field dynamically
        };
        newCompositions[compositionIndex].components_composition_supplier = newSuppliers;
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };


    // Handle file upload for multiple files
    // Store file objects without uploading them yet
    const handleFileUpload = (compositionIndex, supplierIndex, e) => {
        const newFiles = Array.from(e.target.files);
        const newCompositions = [...lotsFormData.components_composition];
        const newSuppliers = [...newCompositions[compositionIndex].components_composition_supplier];
        const existingFiles = newSuppliers[supplierIndex].files || [];

        // Store the files with their original and internal filenames
        const filesWithMetadata = newFiles.map(file => ({
            file, // The File object
            original_filename: file.name, // The original file name
            internal_filename: `${file.name}`, // Generate a temporary internal filename
            isLocal: true, // Mark as a local file
        }));


        // Store the files temporarily in state (without uploading them yet)
        newSuppliers[supplierIndex].files = [...existingFiles, ...filesWithMetadata];
        newCompositions[compositionIndex].components_composition_supplier = newSuppliers;
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };




    // Clear selected files
    const handleClearFiles = (compositionIndex, supplierIndex) => {
        const newCompositions = [...lotsFormData.components_composition];
        const newSuppliers = [...newCompositions[compositionIndex].components_composition_supplier];

        // Revoke temporary URLs for local files
        newSuppliers[supplierIndex].files.forEach(file => {
            if (file.isLocal && file.file) {
                URL.revokeObjectURL(URL.createObjectURL(file.file));
            }
        });

        // Clear the files
        newSuppliers[supplierIndex].files = [];
        newCompositions[compositionIndex].components_composition_supplier = newSuppliers;
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });

        // Clear the file input value
        if (fileInputRefs.current[supplierIndex]) {
            fileInputRefs.current[supplierIndex].value = '';
        }
    };

    // Add new composition
    const handleAddComposition = () => {
        setLotsFormData({
            ...lotsFormData,
            components_composition: [
                ...lotsFormData.components_composition,
                {
                    components_composition_id: '',
                    sequence: '',
                    material_id: '',
                    percentage: '',
                    components_composition_supplier: [{
                        components_composition_sub_supplier_id: '',
                        sub_supplier_name: '',
                        country_id: '',
                        state_province: '',
                        transport_type: '',
                        production_process: '',
                        dyeing: '',
                        dyeing_supplier_name: '',
                        dyeing_country_id: '',
                        dyeing_state_province: '',
                        dyeing_transport_type_id: '',
                        files: []
                    }]
                }
            ]
        });
    };



    // Add new supplier within a composition
    const handleAddMaterialSupplier = (compositionIndex) => {
        const newCompositions = [...lotsFormData.components_composition];
        newCompositions[compositionIndex].components_composition_supplier.push({
            name: '',
            country_id: '',
            transport_type: '',
            files: []
        });
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };


    // Remove a composition
    const handleRemoveComposition = (compIndex) => {
        const newCompositions = [...lotsFormData.components_composition];
        newCompositions.splice(compIndex, 1);
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };

    // Remove a supplier
    const handleRemoveMaterialSupplier = (compositionIndex, supplierIndex) => {
        const newCompositions = [...lotsFormData.components_composition];
        newCompositions[compositionIndex].components_composition_supplier.splice(supplierIndex, 1);
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };

    // Fetch material options for the Select dropdown
    const [optionsMaterial, setOptionsMaterial] = useState([]);
    const fetchDataForSelectMaterial = async () => {
        try {
            const response = await axiosPrivate.get(`${baseURL}/materials`);
            const data = response.data;
            const selectOptions = data.map(item => ({
                value: item.material_id,
                label: item.name,
            }));
            setOptionsMaterial(selectOptions);
        } catch (error) {
            console.error('Error fetching materials:', error);
        }
    };

    // Fetch material options for the Select dropdown
    const [optionsCountry, setOptionsCountry] = useState([]);
    const fetchDataForSelectCountry = async () => {
        try {
            const response = await axiosPrivate.get(`${baseURL}/countries`);
            const data = response.data;
            console.log("fetchDataForSelectCountry data: ", data);
            const selectOptions = data.map(item => ({
                value: item.country_id,
                label: item.country,
            }));
            setOptionsCountry(selectOptions);
        } catch (error) {
            console.error('Error fetching materials:', error);
        }
    };

    const optionsTransportType = [
        { value: 1, label: 'Road' },
        { value: 2, label: 'Sea' },
        { value: 3, label: 'Air' },
        { value: 4, label: 'Rail' },
        { value: 5, label: 'Multimodal' }
    ]


    // Toggle dyeing fields based on checkbox
    const handleDyeingCheckboxChange = (compositionIndex, supplierIndex, checked) => {
        const newCompositions = [...lotsFormData.components_composition];
        newCompositions[compositionIndex].components_composition_supplier[supplierIndex].dyeing = checked;
        setLotsFormData({ ...lotsFormData, components_composition: newCompositions });
    };





    return (
        <div className="form-container">

            {/* Lot fields */}

            {lotsFormData.components_composition.map((composition, compIndex) => (
                <div key={compIndex} className="composition-section">
                    <h6 className="d-inline-block">Component Composition #{compIndex + 1}
                        <FontAwesomeIcon
                            icon={faTrashAlt}
                            onClick={!disabled ? () => handleRemoveComposition(compIndex) : undefined}
                            style={{
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                marginLeft: '10px',
                                opacity: disabled ? 0.5 : 1,
                            }}
                        />
                    </h6>

                    <div className="row">
                        <div className="col-2 d-none">
                            <label htmlFor="production_process" className="form-label app_font1">production_process</label>
                            <input type="production_process" name="production_process" className="form-control form-control-sm app_font1" maxLength="15"
                                value={lotsFormData.production_process}
                                onChange={handleLotChange} />
                        </div>

                        <div className="col-3">
                            <label htmlFor="state" className="form-label app_font1 required">Material</label>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                onChange={(value) => handleCompositionChange(compIndex, 'material_id', value)}
                                value={composition.material_id || undefined}
                                allowClear
                                style={{ width: '100%' }}
                                options={optionsMaterial}
                                className="d-block"
                                disabled={disabled}
                            />
                        </div>
                        <div className="col-1 mb-1">
                            <label htmlFor="percentage" className="form-label app_font1 required">%</label>
                            <input
                                type="text"
                                name="percentage"
                                className="form-control form-control-sm app_font1"
                                maxLength="30"
                                value={composition.percentage}
                                onChange={(e) => handleCompositionChange(compIndex, e.target.name, e.target.value)}
                                disabled={disabled}
                            />
                        </div>

                        <div className="col-2 d-none">
                            <label htmlFor="components_composition_id" className="form-label app_font1">components_composition_id</label>
                            <input type="text" name="components_composition_id" className="form-control form-control-sm app_font1" disabled
                                value={composition.components_composition_id}
                                onChange={handleLotChange} />
                        </div>
                    </div>

                    {composition.components_composition_supplier.map((supplier, suppIndex) => (


                        <div key={suppIndex} className="mb-1">




                            <div className="row">
                                {/* Left Block: Two Lines */}
                                <div className="col-8 mb-1">
                                    {/* First Line: sub_supplier_name and transport_type */}
                                    <div className="row mb-2">
                                        <div className="col-8">
                                            <label htmlFor="name" className="form-label app_font1 required">Supplier #{suppIndex + 1} Name</label>
                                            <input
                                                type="text"
                                                name="sub_supplier_name"
                                                className="form-control form-control-sm app_font1"
                                                maxLength="50"
                                                value={supplier.sub_supplier_name}
                                                onChange={(e) => handleMaterialSupplierChange(compIndex, suppIndex, e.target.name, e.target.value)}
                                                required
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-3">
                                            <label htmlFor="transport_type" className="form-label app_font1 required">Transport</label>
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                onChange={(value) => handleMaterialSupplierChange(compIndex, suppIndex, 'transport_type', value)}
                                                value={supplier.transport_type || undefined}
                                                allowClear
                                                style={{ width: '100%' }}
                                                options={optionsTransportType}
                                                className="d-block"
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>

                                    {/* Second Line: country and state_province */}
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="country_id" className="form-label app_font1 required">Country</label>
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                onChange={(value) => handleMaterialSupplierChange(compIndex, suppIndex, 'country_id', value)}
                                                value={supplier.country_id || undefined}
                                                allowClear
                                                style={{ width: '100%' }}
                                                options={optionsCountry}
                                                className="d-block"
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="state_province" className="form-label app_font1 required">State/Province</label>
                                            <input
                                                type="text"
                                                name="state_province"
                                                className="form-control form-control-sm app_font1"
                                                maxLength="30"
                                                value={supplier.state_province}
                                                onChange={(e) => handleMaterialSupplierChange(compIndex, suppIndex, e.target.name, e.target.value)}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                </div>


                                {/* Right Block: File Input and File List */}
                                <div className="col-3 mb-1">
                                    <label className="form-label app_font1">Upload Files: Certificate of Origin, Quality tests, Transaction Certificates (TC); other</label>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={(e) => handleFileUpload(compIndex, suppIndex, e)}
                                        ref={(el) => (fileInputRefs.current[suppIndex] = el)}
                                        className="form-control form-control-sm"
                                        disabled={disabled}
                                    />
                                    {supplier.files && supplier.files.length > 0 && (
                                        <div className="mt-2">
                                            <ul className="list-unstyled mb-1">
                                                {supplier.files.map((file, fileIndex) => {

                                                    const fileUrl = file.isLocal
                                                        ? URL.createObjectURL(file.file)
                                                        : `${process.env.REACT_APP_FILE_SERVER_BASE_URL}/${file.internal_filename}`;
                                                    return (
                                                        <li key={fileIndex}>
                                                            <a
                                                                href={fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                download={file.original_filename}
                                                            >
                                                                {file.original_filename}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleClearFiles(compIndex, suppIndex)}
                                                disabled={disabled}
                                            >
                                                Clear Files
                                            </button>
                                        </div>
                                    )}
                                </div>



                            </div>





                            <div className="row">
                                <div className="col-2 mt-3">
                                    <label className="form-label app_font1 me-2">Dyeing (if applicable)</label>
                                    <input
                                        type="checkbox"
                                        checked={!!supplier.dyeing}
                                        onChange={(e) => handleDyeingCheckboxChange(compIndex, suppIndex, e.target.checked)}
                                        disabled={disabled}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                {supplier.dyeing && (
                                    <>
                                        <div className="col-3">
                                            <label htmlFor="dyeing_supplier_name" className="form-label app_font1">Supplier Name</label>
                                            <input
                                                type="text"
                                                name="dyeing_supplier_name"
                                                className="form-control form-control-sm"
                                                value={supplier.dyeing_supplier_name || ''}
                                                onChange={(e) => handleMaterialSupplierChange(compIndex, suppIndex, 'dyeing_supplier_name', e.target.value)}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-2">
                                            <label htmlFor="dyeing_country_id" className="form-label app_font1">Country</label>
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                onChange={(value) => handleMaterialSupplierChange(compIndex, suppIndex, 'dyeing_country_id', value)}
                                                value={supplier.dyeing_country_id || undefined}
                                                allowClear
                                                style={{ width: '100%' }}
                                                options={optionsCountry}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-2">
                                            <label htmlFor="dyeing_state_province" className="form-label app_font1">State/Province</label>
                                            <input
                                                type="text"
                                                name="dyeing_state_province"
                                                className="form-control form-control-sm"
                                                value={supplier.dyeing_state_province || ''}
                                                onChange={(e) => handleMaterialSupplierChange(compIndex, suppIndex, 'dyeing_state_province', e.target.value)}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className="col-2 mb-1">
                                            <label htmlFor="dyeing_transport_type_id" className="form-label app_font1">Transport Type</label>
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                onChange={(value) => handleMaterialSupplierChange(compIndex, suppIndex, 'dyeing_transport_type_id', value)}
                                                value={supplier.dyeing_transport_type_id || undefined}
                                                allowClear
                                                style={{ width: '100%' }}
                                                options={optionsTransportType}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            <div style={{ textAlign: 'left' }}> {/* or 'center' or 'right' depending on your design */}
                <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={handleAddComposition}
                    disabled={disabled}
                >
                    Add Material
                </button>
            </div>



            <OkModal
                showModal={showOkModal}
                hideModal={handleHideOkModal}
                message={okMessage}
                type={typeOkModal}
                header=""
            />
        </div>
    );



};

export default LotsForm;
